import { PageTitleRow } from "../../components/layout/PageTitleRow";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Ban, Check, ChevronDown, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../components/layout";
import { ContentCard, LoadingState, ui } from "../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { cn } from "../../lib/utils";
import { hrmsGet } from "../../api/api";
import {
  fetchCanonicalRoles,
  fetchCitiesByState,
  fetchRoles,
  fetchShifts,
  fetchStates,
  getOnboardingRequest,
  updateOnboardingRequest,
  updateOnboardingRequestStatus,
} from "../../services/onboardingRequestService";
import {
  SUBMITTED_FIELDS,
  fieldLabel,
  isDateField,
  isReadOnlyField,
  isReviewableRequest,
  requestName,
  requestStatusOf,
  shiftLabel,
  submittedPayload,
} from "./onboardingRequestFields";
import { RequestStatusBadge, formatRequestDate } from "./RequestStatusBadge";

/**
 * The status an approved request lands on: approving IS onboarding, and the
 * update endpoint records both the completed request and that status in one
 * call. Rejecting only moves the status, so it goes through the status
 * endpoint instead.
 */
/** The list this screen was opened from, and returns to once decided. */
const REQUESTS_PATH = "/hrms-team/employees/requests";

/**
 * What a picked submitted field may be chosen from, as {value, label}.
 *
 * The label is always the name a person would recognise; the value is what the
 * update body carries, and that differs per field — a manager travels as
 * its id, a state and a district as their names — so the row is read by the
 * caller's own optionValue. (Not "valueOf": that one is inherited from
 * Object.prototype, so a field that did not set it would get that instead of
 * the default below.)
 *
 * What the candidate typed stays on the list even when the master does not
 * know it: they filled the form unaided and offline, and dropping their answer
 * because it is unrecognised would erase it from a screen the reviewer is
 * meant to be checking. It sits first, already selected, until it is replaced.
 */
function placeOptions(rows, current, optionValue = (row) => String(row.name)) {
  const options = rows.map((row) => ({ value: optionValue(row), label: String(row.name) }));
  const chosen = String(current ?? "").trim();
  const known = options.some(
    (option) => option.value.toLowerCase() === chosen.toLowerCase()
  );
  return chosen && !known ? [{ value: chosen, label: chosen }, ...options] : options;
}

/** GET /saas/hrms/current-employment/search/canonicalRoleName — HR02S, as {id, name}. */
async function fetchEmployeesByCanonicalRole(searchTerm) {
  const params = new URLSearchParams({ searchTerm, page: "0", size: "10" });
  const response = await hrmsGet(
    `/current-employment/search/canonicalRoleName?${params.toString()}`,
    "HR02S"
  );
  const data = response?.data ?? response;
  const rows = Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
  return rows
    .map((row) => {
      const id = row?.empId ?? row?.employeeId ?? row?.emp_Id ?? row?.id;
      const fullName = [row?.firstName ?? row?.first_name, row?.lastName ?? row?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      return { id, name: row?.name ?? row?.employeeName ?? (fullName || `#${id}`) };
    })
    .filter((row) => row.id != null);
}

const ONBOARDED = "ONBOARDED";
const REJECTED = "REJECTED";

/**
 * One onboarding request — read what was submitted, assign what the
 * organization owns, decide.
 *
 * The candidate filled the whole form outside the product through a tokenised
 * link, unverified and unaided, so their answers are shown first AND are
 * editable: a transposed digit in a pincode should be corrected here, not
 * bounced back to the candidate. Whatever is on screen is what the update
 * endpoint is sent.
 *
 * APPROVING ONBOARDS THE EMPLOYEE, which is why the assignment is required
 * before it and saved before the decision is sent: a request approved without
 * it would create an employee with no role, no manager and no shift.
 */

/** Several values, one string — the update body takes a string per field. */
const joinValues = (values) => values.join(",");

const labelClass = "mb-1.5 block text-xs font-semibold text-foreground";

/**
 * One pick out of a tenant master.
 *
 * Every master answers a different shape, so each caller hands over plain
 * {value, label} options and this only has to draw them.
 */
function AssignmentSelect({ label, placeholder, options, value, disabled, required, onChange }) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>
      <Select value={value || undefined} disabled={disabled} onValueChange={onChange}>
        <SelectTrigger className="h-10 w-full rounded-lg border border-(--color-text-border) bg-white text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-60 overflow-y-auto">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Searchable checkbox dropdown; each caller retains its existing selection values. */
function AssignmentMultiSelect({ label, options, selected, disabled, onToggle }) {
  const { t } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState("");
  const availableOptions = Array.from(new Map(options.map((option) => [option.value, option])).values());
  selected.forEach((value) => {
    if (!availableOptions.some((option) => option.value === value)) {
      availableOptions.push({ value, label: value });
    }
  });
  const selectedLabels = selected.map((value) =>
    availableOptions.find((option) => option.value === value)?.label || value
  ).join(", ");
  const query = search.trim().toLocaleLowerCase();
  const filteredOptions = availableOptions.filter((option) =>
    String(option.label).toLocaleLowerCase().includes(query)
  );

  return (
    <div className="min-w-0">
      <label id={`${id}-label`} htmlFor={id} className={labelClass}>
        {label}
        <span className="text-rose-500"> *</span>
      </label>
      <Popover onOpenChange={(open) => { if (!open) setSearch(""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            title={selectedLabels}
            aria-label={`${label}: ${selectedLabels || t("onboarding.select")}`}
            className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-(--color-text-border) bg-white px-3 text-left text-sm text-foreground focus:outline-hidden focus:ring-2 focus:ring-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="min-w-0 truncate">{selectedLabels || t("onboarding.select")}</span>
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
          <input data-page-search="true"
            type="search"
            value={search}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("common.search")}
            aria-label={`${t("common.search")}: ${label}`}
            className="mb-2 h-10 w-full rounded-lg border border-(--color-text-border) bg-white px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-accent/10"
          />
          <div role="group" aria-labelledby={`${id}-label`} className="max-h-60 overflow-y-auto">
            {filteredOptions.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-foreground transition hover:bg-workspace/40 focus-within:bg-workspace/40",
                  disabled && "cursor-not-allowed opacity-60"
                )}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  disabled={disabled}
                  onChange={() => onToggle(option.value)}
                  className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
                />
                <span className="min-w-0 wrap-anywhere">{option.label}</span>
              </label>
            ))}
            {!filteredOptions.length && (
              <p className="px-2 py-2 text-sm per-file-hint">
                {query
                  ? t("common.noResultsFound", { defaultValue: "No results found" })
                  : t("onboarding.noRolesAvailable")}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** A titled part of the details card, set off from the part above by a rule. */
function DetailSection({ title, children }) {
  return (
    <section className="border-t border-(--color-text-border) pt-5">
      <h3 className="mb-4 text-sm font-bold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

export function OnboardingRequestDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestId } = useParams();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [roles, setRoles] = useState([]);
  const [canonicalRoles, setCanonicalRoles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [lookupError, setLookupError] = useState("");

  /** The submitted half, as the reviewer may have corrected it. */
  const [details, setDetails] = useState({});

  const [assignment, setAssignment] = useState({
    canonicalRoleNames: [],
    roleIds: [],
    managerId: "",
    hrId: "",
    shiftId: "",
    effectiveFrom: "",
    effectiveTo: "",
  });

  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");

  /**
   * Leave for the list, carrying the outcome.
   *
   * A decided request has nothing left to review here — staying would show a
   * read-only form the reviewer has no reason to look at. The message travels
   * as router state so the confirmation is not lost on the way out, and the
   * entry is replaced so Back does not return to the screen just finished.
   */
  const returnToList = (message) =>
    navigate(REQUESTS_PATH, { replace: true, state: { flash: message } });

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    getOnboardingRequest(requestId)
      .then((loaded) => {
        setRequest(loaded);
        setDetails(submittedPayload(loaded));
        // Whatever the request already carries is where the form starts.
        setAssignment((current) => ({
          ...current,
          canonicalRoleNames: loaded?.canonicalRoleName
            ? String(loaded.canonicalRoleName).split(",").filter(Boolean)
            : [],
          roleIds: loaded?.roleId ? String(loaded.roleId).split(",").filter(Boolean) : [],
          managerId: loaded?.managerId == null ? "" : String(loaded.managerId),
          hrId: loaded?.hrId == null ? "" : String(loaded.hrId),
          shiftId: loaded?.shiftId == null ? "" : String(loaded.shiftId),
          effectiveFrom: loaded?.effectiveFrom ?? "",
          effectiveTo: loaded?.effectiveTo ?? "",
        }));
      })
      .catch((error) => {
        console.error("Failed to load onboarding request:", error);
        setRequest(null);
        setLoadError(error.message || t("onboardingRequests.errorLoad"));
      })
      .finally(() => setLoading(false));
  }, [requestId, t]);

  useEffect(() => load(), [load]);

  /**
   * The lists the assignment is chosen from, all the tenant's own.
   *
   * Loaded independently: they are separate masters, and one that fails
   * or is empty must not take the others down with it — which is what a
   * single Promise.all would do, leaving every dropdown blank because of one.
   */
  useEffect(() => {
    let active = true;

    const lookups = [
      ["canonicalRoles", fetchCanonicalRoles, setCanonicalRoles],
      ["roles", fetchRoles, setRoles],
      ["shifts", fetchShifts, setShifts],
      ["managers", () => fetchEmployeesByCanonicalRole("manager"), setManagers],
      ["hrs", () => fetchEmployeesByCanonicalRole("hr"), setHrs],
      ["states", fetchStates, setStates],
    ];

    Promise.allSettled(
      lookups.map(([name, read, apply]) =>
        read().then((rows) => {
          if (active) apply(rows);
          // An empty master is not an error, but it is why a dropdown is
          // empty — worth saying once in the console rather than leaving the
          // reviewer to guess.
          if (!rows.length) console.warn(`Onboarding assignment: ${name} returned no rows`);
          return name;
        })
      )
    ).then((results) => {
      if (!active) return;
      const failed = results.filter((result) => result.status === "rejected");
      failed.forEach((result) => console.error("Assignment lookup failed:", result.reason));
      if (failed.length) {
        setLookupError(
          failed[0].reason?.message || t("onboardingRequests.errorLoadLookups")
        );
      }
    });

    return () => {
      active = false;
    };
  }, [t]);

  /**
   * The id of the submitted state, found by name.
   *
   * The request carries the state as text — that is what the employee record
   * stores and what travels back on update — while the districts endpoint is
   * keyed by id. Matching here keeps the payload unchanged and still lets the
   * district list be fetched. A state that matches nothing simply has no
   * districts to offer, which is the honest answer.
   */
  const stateId = useMemo(() => {
    const chosen = String(details.state ?? "").trim().toLowerCase();
    if (!chosen) return "";
    const match = states.find((row) => String(row.name).trim().toLowerCase() === chosen);
    return match?.id == null ? "" : String(match.id);
  }, [details.state, states]);

  /** Districts follow the state, and are re-read whenever it changes. */
  useEffect(() => {
    let active = true;

    if (!stateId) {
      setDistricts([]);
      return undefined;
    }

    fetchCitiesByState(stateId)
      .then((rows) => {
        if (active) setDistricts(rows);
      })
      .catch((error) => {
        console.error("Failed to load districts for the state:", error);
        if (active) setDistricts([]);
      });

    return () => {
      active = false;
    };
  }, [stateId]);

  const setDetail = (key, value) => {
    setActionError("");
    setDetails((current) => ({
      ...current,
      [key]: value,
      // A district belongs to one state: keeping the old one after the state
      // changes would send an address that does not exist.
      ...(key === "state" && value !== current.state ? { district: "" } : null),
    }));
  };

  const setAssigned = (key, value) => {
    setActionError("");
    setAssignment((current) => ({ ...current, [key]: value }));
  };

  const toggle = (key, value) =>
    setAssignment((current) => {
      setActionError("");
      const list = current[key];
      return {
        ...current,
        [key]: list.includes(value)
          ? list.filter((entry) => entry !== value)
          : [...list, value],
      };
    });

  /** Everything the employee record is created from. */
  const assignmentComplete =
    assignment.canonicalRoleNames.length > 0 &&
    assignment.roleIds.length > 0 &&
    /^\d+$/.test(assignment.managerId.trim()) &&
    /^\d+$/.test(assignment.hrId.trim()) &&
    String(assignment.shiftId).trim() !== "" &&
    assignment.effectiveFrom !== "" &&
    assignment.effectiveTo !== "";

  /**
   * Approve: the completed request and its new status, in one update.
   *
   * Everything the employee is created from travels together — the corrected
   * details, the assignment, and requestStatus ONBOARDED — so there is no
   * moment where a request is marked onboarded against values that failed to
   * save.
   */
  const approve = async () => {
    if (!assignmentComplete) {
      setActionError(t("onboardingRequests.errorAssignmentRequired"));
      return;
    }

    setBusy(ONBOARDED);
    setActionError("");
    try {
      await updateOnboardingRequest(requestId, {
        ...details,
        canonicalRoleName: joinValues(assignment.canonicalRoleNames),
        roleId: joinValues(assignment.roleIds),
        managerId: Number(assignment.managerId),
        hrId: Number(assignment.hrId),
        shiftId: String(assignment.shiftId),
        effectiveFrom: assignment.effectiveFrom,
        effectiveTo: assignment.effectiveTo,
        requestStatus: ONBOARDED,
      });
      returnToList(t("onboardingRequests.approved"));
    } catch (error) {
      console.error("Failed to onboard the request:", error);
      setActionError(error.message || t("onboardingRequests.errorDecision"));
    } finally {
      setBusy("");
    }
  };

  /** Reject: the status alone. Nothing else about the request is sent. */
  const reject = async () => {
    setBusy(REJECTED);
    setActionError("");
    try {
      await updateOnboardingRequestStatus(requestId, REJECTED);
      returnToList(t("onboardingRequests.rejected"));
    } catch (error) {
      console.error("Failed to reject the request:", error);
      setActionError(error.message || t("onboardingRequests.errorDecision"));
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return (
      <AppLayout formLayout="standard">
        <PageTitleRow className="mb-5" back={<PageBackLink to={REQUESTS_PATH} />}><h1>{t("onboardingRequests.requestDetails", "Onboarding request details")}</h1></PageTitleRow>
        <ContentCard>
          <LoadingState message={t("onboardingRequests.loading")} />
        </ContentCard>
      </AppLayout>
    );
  }

  if (loadError || !request) {
    return (
      <AppLayout formLayout="standard">
        <PageTitleRow className="mb-5" back={<PageBackLink to={REQUESTS_PATH} />}><h1>{t("onboardingRequests.requestDetails", "Onboarding request details")}</h1></PageTitleRow>
        <ContentCard bodyClassName="form-section text-center">
          <p className="text-sm font-bold text-foreground">
            {t("onboardingRequests.notFoundTitle")}
          </p>
          <p className="mt-1 text-sm per-file-hint">
            {loadError || t("onboardingRequests.notFoundMessage")}
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-4 inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-(--color-text-border) px-4 text-sm font-semibold text-foreground transition hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            {t("onboardingRequests.retry")}
          </button>
        </ContentCard>
      </AppLayout>
    );
  }

  // Reached by typing the URL: the list does not open these.
  if (!isReviewableRequest(request)) {
    return (
      <AppLayout formLayout="standard">
        <PageTitleRow className="mb-5" back={<PageBackLink to={REQUESTS_PATH} />}><h1>{t("onboardingRequests.requestDetails", "Onboarding request details")}</h1></PageTitleRow>
        <ContentCard bodyClassName="form-section text-center">
          <p className="text-sm font-bold text-foreground">
            {t("onboardingRequests.notSubmittedTitle")}
          </p>
          <p className="mx-auto mt-1 max-w-[460px] text-sm per-file-hint">
            {t("onboardingRequests.notSubmittedMessage")}
          </p>
        </ContentCard>
      </AppLayout>
    );
  }

  const deciding = Boolean(busy);

  /**
   * The control a submitted field is edited with.
   *
   * State and district are picked from the tenant's own masters
   * rather than typed: they decide where the employee record can be filed, and
   * free text from an external form spells them however it likes. The rest stay
   * plain inputs — there is no list to hold a house number to.
   */
  const detailControl = (key) => {
    if (isReadOnlyField(key)) {
      return (
        <p
          id={`detail-${key}`}
          className="flex h-10 items-center rounded-lg border border-(--color-text-border) bg-gray-50 px-3 text-sm font-semibold per-file-hint"
        >
          {details[key] || "—"}
        </p>
      );
    }

    const picked = {
      state: { rows: states, placeholder: "onboardingRequests.selectState" },
      district: { rows: districts, placeholder: "onboardingRequests.selectDistrict" },
    }[key];

    if (picked) {
      const options = placeOptions(picked.rows, details[key], picked.optionValue);
      // A district with nothing to offer is waiting on its state, not broken.
      const awaitingState = key === "district" && options.length === 0;

      return (
        <Select
          // An id read back as a number would match no option as it stands.
          value={String(details[key] ?? "") || undefined}
          disabled={deciding || awaitingState}
          onValueChange={(value) => setDetail(key, value)}
        >
          <SelectTrigger
            id={`detail-${key}`}
            className="h-10 w-full rounded-lg border border-(--color-text-border) bg-white text-sm"
          >
            <SelectValue
              placeholder={t(
                awaitingState ? "onboardingRequests.selectStateFirst" : picked.placeholder
              )}
            />
          </SelectTrigger>
          <SelectContent className="max-h-60 overflow-y-auto">
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <input
        id={`detail-${key}`}
        type={isDateField(key) ? "date" : "text"}
        value={details[key] ?? ""}
        disabled={deciding}
        onChange={(event) => setDetail(key, event.target.value)}
        className={cn(ui.input, "h-10 w-full")}
      />
    );
  };

  return (
    <AppLayout formLayout="standard" className="space-y-5">

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <PageTitleRow back={<PageBackLink to={REQUESTS_PATH} />}>
            <h1 className={ui.pageTitle}>{requestName(request) || `#${requestId}`}</h1>
          </PageTitleRow>
          <p className="mt-1 text-[12px] per-file-hint">
            {[request.email, formatRequestDate(request.createdAt, t)].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="shrink-0">
          <RequestStatusBadge status={requestStatusOf(request)} />
        </div>
      </div>

      {/* What the candidate submitted — first, because the assignment is a
          judgement about it, and editable so a typo is fixed rather than
          bounced. */}
      <ContentCard title={t("onboardingRequests.submittedDetails")} bodyClassName="form-section">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 form-grid">
          {SUBMITTED_FIELDS.map((key) => (
            <div key={key} className="min-w-0">
              <label htmlFor={`detail-${key}`} className={labelClass}>
                {fieldLabel(key)}
              </label>
              {detailControl(key)}
            </div>
          ))}
        </div>
      </ContentCard>

      {/* What the organization assigns. */}
      <ContentCard
        title={t("onboardingRequests.detailsToBeFilled", "Details to be filled")}
        bodyClassName="form-section"
      >
        {lookupError ? (
          <p className="mb-3 text-[12px] font-semibold text-rose-600">{lookupError}</p>
        ) : null}

        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 form-grid">
            <AssignmentMultiSelect
              label={t("onboarding.labels.canonicalRoleName")}
              options={canonicalRoles.map((role) => ({ value: String(role.name), label: role.name }))}
              selected={assignment.canonicalRoleNames}
              disabled={deciding}
              onToggle={(value) => toggle("canonicalRoleNames", value)}
            />

            <AssignmentMultiSelect
              label={t("onboarding.labels.role")}
              options={roles.map((role) => ({ value: String(role.id), label: role.name }))}
              selected={assignment.roleIds}
              disabled={deciding}
              onToggle={(value) => toggle("roleIds", value)}
            />
          </div>

          <DetailSection title={t("onboardingRequests.employeeDetails", "Employee Details")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 form-grid">
              <AssignmentSelect
                label={t("common.manager")}
                placeholder={t("formActions.searchManager")}
                options={placeOptions(managers, assignment.managerId, (row) => String(row.id))}
                value={assignment.managerId}
                disabled={deciding}
                required
                onChange={(value) => setAssigned("managerId", value)}
              />

              <AssignmentSelect
                label={t("onboarding.labels.hr")}
                placeholder={t("formActions.searchHr")}
                options={placeOptions(hrs, assignment.hrId, (row) => String(row.id))}
                value={assignment.hrId}
                disabled={deciding}
                required
                onChange={(value) => setAssigned("hrId", value)}
              />
            </div>
          </DetailSection>

          <DetailSection title={t("onboardingRequests.shiftDetails", "Shift Details")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 form-grid">
              <AssignmentSelect
                label={t("onboardingRequests.shift")}
                placeholder={t("onboardingRequests.selectShift")}
                options={shifts.map((shift) => ({
                  value: String(shift.id),
                  label: shiftLabel(shift),
                }))}
                value={assignment.shiftId ? String(assignment.shiftId) : ""}
                disabled={deciding}
                required
                onChange={(value) => setAssigned("shiftId", value)}
              />

              <div>
                <label htmlFor="effectiveFrom" className={labelClass}>
                  {t("onboardingRequests.effectiveFrom")}
                  <span className="text-rose-500"> *</span>
                </label>
                <input
                  id="effectiveFrom"
                  type="date"
                  value={assignment.effectiveFrom}
                  disabled={deciding}
                  onChange={(event) => setAssigned("effectiveFrom", event.target.value)}
                  className={cn(ui.input, "h-10 w-full")}
                />
              </div>
              <div>
                <label htmlFor="effectiveTo" className={labelClass}>
                  {t("onboardingRequests.effectiveTo")}
                  <span className="text-rose-500"> *</span>
                </label>
                <input
                  id="effectiveTo"
                  type="date"
                  value={assignment.effectiveTo}
                  min={assignment.effectiveFrom || undefined}
                  disabled={deciding}
                  onChange={(event) => setAssigned("effectiveTo", event.target.value)}
                  className={cn(ui.input, "h-10 w-full")}
                />
              </div>
            </div>
          </DetailSection>
        </div>
      </ContentCard>

      {/* Decision */}
      <div className="flex flex-col gap-3">
        {actionError ? (
          <p className="text-[12px] font-semibold text-rose-600">{actionError}</p>
        ) : null}

        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={reject}
            disabled={deciding}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-rose-300 bg-white px-5 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Ban className="h-4 w-4" />
            {busy === REJECTED ? t("onboardingRequests.rejecting") : t("onboardingRequests.reject")}
          </button>
          <button
            type="button"
            onClick={approve}
            disabled={deciding}
            className={cn(ui.primaryBtn, "h-11 px-6")}
          >
            <Check className="h-4 w-4" />
            {busy === ONBOARDED ? t("onboardingRequests.approving") : t("onboardingRequests.approve")}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}

export default OnboardingRequestDetailPage;
